import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { useForm } from "react-hook-form";
import { Fade } from "react-awesome-reveal";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { BsGenderAmbiguous } from "react-icons/bs";
import EmailIcon from "../../../assets/icons/message.svg";
import UserIcon from "../../../assets/icons/profile.svg";
import UsersIcon from "../../../assets/icons/users.svg";
import CalendarIcon from "../../../assets/icons/calendar.svg";
import PhoneIcon from "../../../assets/icons/call.svg";
import Input from "../../form/input";
import Select from "../../form/select";
import LoadingButton from "../../common/loadingButton";
import { genderOptions } from "../../../data/SelectOptions";
import apiClient from "../../../services/apiClient";
import useStore from "../../../features/store";
import {
  formateDateToGregorian,
  formateDateToJalali,
} from "../../../utils/Functions";
import DatePickerInput from "../../form/datePicker";

const schema = yup.object().shape({
  first_name: yup.string().required("نام الزامی است"),
  last_name: yup.string().required("نام‌خانوادگی الزامی است"),
  gender: yup.string().required("جنسیت الزامی است"),
  email: yup.string().email("فرمت ایمیل اشتباه است"),
});

const EditData = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get("redirect");

  //@ts-ignore
  const { profile, fetchProfile } = useStore();
  const [date, setDate] = useState<any>(
    profile.birth_day ? formateDateToJalali(profile.birth_day) : ""
  );

  const {
    handleSubmit,
    formState: { errors },
    control,
    ...form
  } = useForm({
    defaultValues: {
      first_name: profile.user.first_name || "",
      last_name: profile.user.last_name || "",
      email: profile.user.email || "",
      gender: profile.gender || "UNKNOWN",
    },
    resolver: yupResolver(schema),
    mode: "onTouched",
  });

  async function updateUserProfile(profileData: any) {
    setLoading(true);

    const changedData = {};
    if (profileData.first_name !== profile.user.first_name)
      //@ts-ignore
      changedData.first_name = profileData.first_name;
    if (profileData.last_name !== profile.user.last_name)
      //@ts-ignore
      changedData.last_name = profileData.last_name;
    if (date && formateDateToJalali(profile.birth_day) !== date)
      //@ts-ignore
      changedData.birth_day = formateDateToGregorian(date);
    if (profileData.email !== profile.user.email)
      //@ts-ignore
      changedData.email = profileData.email;
    if (profileData.gender !== profile.gender)
      //@ts-ignore
      changedData.gender = profileData.gender;

    if (Object.keys(changedData).length === 0) {
      setLoading(false);
      return;
    }

    try {
      const response = await apiClient.patch("/profile", changedData);
      if (response.status === 200) {
        fetchProfile();
        if (redirectUrl) {
          navigate(`/${redirectUrl}`);
        } else {
          navigate("/profile");
          toast.success("پروفایل ویرایش شد");
        }
      }
    } catch (error) {
      //@ts-ignore
      if (error?.response?.data?.email?.length > 0) {
        toast.error("ایمیل وارد شده تکراری است");
      } else {
        toast.error("مشکلی در ثبت اطلاعات پیش آمد");
      }
    } finally {
      setLoading(false);
    }
  }

  const onSubmit = (data: any) => {
    updateUserProfile(data);
  };

  return (
    <Fade triggerOnce>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex-1 w-full max-w-lg mt-5 flex flex-col justify-between mb-10"
      >
        <div className="flex-1 mb-2">
          <span className="text-gray-800 text-sm">شماره همراه</span>
          <div className="bg-gray-200 p-3 rounded-lg mt-1 flex items-center">
            <img src={PhoneIcon} />
            <p className="text-gray-600 mr-3 text-left flex-1">
              {profile?.mobile}
            </p>
          </div>
        </div>

        <div className="flex-1 mb-5">
          <Input
            form={form}
            errors={errors}
            title="نام"
            icon={<img src={UserIcon} alt="نام" />}
            id="first_name"
            type="text"
            direction="rtl"
          />
          <Input
            form={form}
            errors={errors}
            title="نام‌خانوادگی"
            icon={<img src={UsersIcon} alt="نام‌خانوادگی" />}
            id="last_name"
            type="text"
            direction="rtl"
          />
          <DatePickerInput
            title="تاریخ تولد"
            id="birth_day"
            date={date}
            setDate={setDate}
            icon={<img src={CalendarIcon} alt="سن" />}
          />
          <Select
            form={form}
            errors={errors}
            title="جنسیت"
            icon={<BsGenderAmbiguous size={23} />}
            id="gender"
            options={genderOptions}
          />
          <Input
            form={form}
            errors={errors}
            title="ایمیل"
            icon={<img src={EmailIcon} alt="ایمیل" />}
            id="email"
            type="email"
            direction="ltr"
          />
        </div>

        <LoadingButton
          title="ویرایش"
          type="submit"
          disabled={loading}
          loading={loading}
        />
      </form>
    </Fade>
  );
};

export default EditData;
